import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import {
    buildReconciliation,
    getReconciliationBatches,
    getReconciliationList
} from '../../services/Configs/ExcelImports';

const RECONCILIATION_STATUS_LABELS = {
    matched: 'Coincidencia exacta',
    matched_internal: 'Coincidencia interna',
    missing_folio: 'Folio faltante',
    unassigned_folio: 'Folio sin ítem asignado',
    missing_item: 'Ítem faltante',
    multiple_candidates: 'Múltiples candidatos',
    resolved: 'Resuelto',
    pending_review: 'Pendiente de revisión',
    ignored: 'Ignorado'
};

const FOLIO_TYPE_LABELS = {
    Nn: 'Nn',
    FF: 'FF',
    UNKNOWN: 'Desconocido'
};

const getStatusLabel = (status) =>
    RECONCILIATION_STATUS_LABELS[status] || status || '—';

const getFolioTypeLabel = (type) =>
    FOLIO_TYPE_LABELS[type] || type || '—';

const InventoryReconciliation = () => {
    const [batches, setBatches] = useState([]);
    const [selectedBatch, setSelectedBatch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [folioTypeFilter, setFolioTypeFilter] = useState('');
    const [search, setSearch] = useState('');

    const [onlyInconsistencies, setOnlyInconsistencies] = useState(false);
    const [onlyActiveWithoutItem, setOnlyActiveWithoutItem] = useState(false);
    const [onlySold, setOnlySold] = useState(false);
    const [onlyCourtesy, setOnlyCourtesy] = useState(false);
    const [onlyExcelInStock, setOnlyExcelInStock] = useState(false);

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({
        total: 0,
        matched: 0,
        inconsistencies: 0,
        active_without_item: 0,
        sold: 0,
        courtesy: 0,
        not_visible: 0
    });

    const [loading, setLoading] = useState(false);
    const [building, setBuilding] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const roleId = Number(sessionStorage.getItem('roleId') || 0);
    const canBuildReconciliation = roleId === 1;

    const loadBatches = async () => {
        const response = await getReconciliationBatches();
        if (response?.result) {
            setBatches(response.data || []);
        }
    };

    const loadList = async () => {
        if (!selectedBatch) {
            setRows([]);
            return;
        }

        setLoading(true);
        setErrorMessage('');

        try {
            const response = await getReconciliationList({
                import_batch_id: selectedBatch,
                reconciliation_status: statusFilter,
                excel_folio_type: folioTypeFilter,
                search,
                only_inconsistencies: onlyInconsistencies ? 1 : 0,
                only_active_without_item: onlyActiveWithoutItem ? 1 : 0,
                only_sold: onlySold ? 1 : 0,
                only_courtesy: onlyCourtesy ? 1 : 0,
                only_excel_in_stock: onlyExcelInStock ? 1 : 0,
                page,
                pageSize
            });

            if (!response?.result) {
                setErrorMessage(response?.message || 'No fue posible cargar el consolidado.');
                return;
            }

            const listRows = response?.data?.rows || [];
            const responseTotal = response?.data?.total || 0;
            const responseTotalPages = response?.data?.totalPages || 1;

            setRows(listRows);
            setTotalRows(responseTotal);
            setTotalPages(responseTotalPages);

            setSummary({
                total: responseTotal,
                matched: listRows.filter((r) => r.reconciliation_status === 'matched').length,
                inconsistencies: responseTotal - listRows.filter((r) => r.reconciliation_status === 'matched').length,
                active_without_item: listRows.filter((r) => r.folio_exists && r.folio_is_active && !r.assigned_item_id).length,
                sold: listRows.filter((r) => r.is_sold).length,
                courtesy: listRows.filter((r) => r.is_courtesy).length,
                not_visible: listRows.filter((r) => !r.is_visible_in_inventory).length
            });
        } catch (error) {
            console.error(error);
            setErrorMessage('Ocurrió un error al cargar el consolidado.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBatches();
    }, []);

    useEffect(() => {
        loadList();
    }, [
        selectedBatch,
        statusFilter,
        folioTypeFilter,
        onlyInconsistencies,
        onlyActiveWithoutItem,
        onlySold,
        onlyCourtesy,
        onlyExcelInStock,
        page,
        pageSize
    ]);

    useEffect(() => {
        setPage(1);
    }, [
        selectedBatch,
        statusFilter,
        folioTypeFilter,
        search,
        onlyInconsistencies,
        onlyActiveWithoutItem,
        onlySold,
        onlyCourtesy,
        onlyExcelInStock,
        pageSize
    ]);

    const handleBuild = async () => {
        if (!selectedBatch) {
            setErrorMessage('Selecciona un lote para generar la conciliación.');
            return;
        }

        setBuilding(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await buildReconciliation({
                import_batch_id: selectedBatch
            });

            if (!response?.result) {
                setErrorMessage(response?.message || 'No fue posible generar la conciliación.');
                return;
            }

            setSuccessMessage(`Conciliación generada correctamente para el lote ${selectedBatch}.`);
            await loadList();
        } catch (error) {
            console.error(error);
            setErrorMessage('Ocurrió un error al generar la conciliación.');
        } finally {
            setBuilding(false);
        }
    };

    const statusVariant = (status) => {
        switch (status) {
            case 'matched': return 'success';
            case 'matched_internal': return 'primary';
            case 'missing_folio': return 'danger';
            case 'unassigned_folio': return 'warning';
            case 'missing_item': return 'secondary';
            case 'multiple_candidates': return 'dark';
            case 'resolved': return 'success';
            default: return 'info';
        }
    };

    const boolBadge = (value, trueLabel = 'Sí', falseLabel = 'No') => (
        <Badge bg={value ? 'success' : 'secondary'}>
            {value ? trueLabel : falseLabel}
        </Badge>
    );

    const getVisiblePages = () => {
        const maxButtons = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxButtons - 1);

        if (end - start < maxButtons - 1) {
            start = Math.max(1, end - maxButtons + 1);
        }

        const pages = [];
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const visiblePages = getVisiblePages();

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0">Inventario consolidado</h5>

                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">{totalRows} registro(s)</span>

                    <Form.Select
                        size="sm"
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        style={{ width: '110px' }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </Form.Select>
                </div>
            </div>

            {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
            {successMessage ? <Alert variant="success">{successMessage}</Alert> : null}

            <Row className="g-3 mb-3">
                <Col xs={12} xl={8}>
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h5 className="mb-3">Filtros del consolidado</h5>

                            <Row className="g-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Lote importado</Form.Label>
                                        <Form.Select
                                            value={selectedBatch}
                                            onChange={(e) => setSelectedBatch(e.target.value)}
                                        >
                                            <option value="">Selecciona un lote...</option>
                                            {batches.map((batch) => (
                                                <option key={batch.import_batch_id} value={batch.import_batch_id}>
                                                    {batch.import_batch_id} ({batch.total_rows})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Estado conciliación</Form.Label>
                                        <Form.Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            <option value="matched">Coincidencia exacta</option>
                                            <option value="matched_internal">Coincidencia interna</option>
                                            <option value="missing_folio">Folio faltante</option>
                                            <option value="unassigned_folio">Folio sin ítem asignado</option>
                                            <option value="missing_item">Ítem faltante</option>
                                            <option value="multiple_candidates">Múltiples candidatos</option>
                                            <option value="resolved">Resuelto</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Tipo de folio</Form.Label>
                                        <Form.Select
                                            value={folioTypeFilter}
                                            onChange={(e) => setFolioTypeFilter(e.target.value)}
                                        >
                                            <option value="">Todos</option>
                                            <option value="Nn">Nn</option>
                                            <option value="FF">FF</option>
                                            <option value="UNKNOWN">Desconocido</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={8}>
                                    <Form.Group>
                                        <Form.Label>Buscar</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Buscar por nombre o folio..."
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={4} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        className="w-100"
                                        onClick={() => {
                                            setPage(1);
                                            loadList();
                                        }}
                                    >
                                        Buscar
                                    </Button>
                                </Col>

                                <Col md={3}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Solo inconsistencias"
                                        checked={onlyInconsistencies}
                                        onChange={(e) => setOnlyInconsistencies(e.target.checked)}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Solo activos sin item"
                                        checked={onlyActiveWithoutItem}
                                        onChange={(e) => setOnlyActiveWithoutItem(e.target.checked)}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Solo vendidos"
                                        checked={onlySold}
                                        onChange={(e) => setOnlySold(e.target.checked)}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Solo cortesía"
                                        checked={onlyCourtesy}
                                        onChange={(e) => setOnlyCourtesy(e.target.checked)}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Solo Excel en stock"
                                        checked={onlyExcelInStock}
                                        onChange={(e) => setOnlyExcelInStock(e.target.checked)}
                                    />
                                </Col>
                            </Row>

                            <div className="d-flex gap-2 mt-4">
                                {canBuildReconciliation && (
                                    <Button
                                        variant="success"
                                        onClick={handleBuild}
                                        disabled={!selectedBatch || building}
                                    >
                                        {building ? 'Generando...' : 'Generar conciliación'}
                                    </Button>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} xl={4}>
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h5 className="mb-3">Resumen</h5>
                            <div className="d-flex flex-wrap gap-2">
                                <Badge bg="secondary">Total: {summary.total}</Badge>
                                <Badge bg="success">Coincidencias: {summary.matched}</Badge>
                                <Badge bg="warning" text="dark">Activos sin item: {summary.active_without_item}</Badge>
                                <Badge bg="danger">Inconsistencias: {summary.inconsistencies}</Badge>
                                <Badge bg="dark">Vendidos: {summary.sold}</Badge>
                                <Badge bg="info">Cortesía: {summary.courtesy}</Badge>
                                <Badge bg="secondary">No visibles: {summary.not_visible}</Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Inventario consolidado</h5>
                        <div className="text-muted small">{totalRows} registro(s)</div>
                    </div>

                    <div className="table-responsive">
                        <Table bordered hover className="align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Pestaña</th>
                                    <th>Producto Excel</th>
                                    <th>Folio Excel</th>
                                    <th>Tipo</th>
                                    <th>Folio sistema</th>
                                    <th>Existe en sistema</th>
                                    <th>Activo</th>
                                    <th>Item asignado</th>
                                    <th>Nombre item</th>
                                    <th>Vendido</th>
                                    <th>Cortesía</th>
                                    <th>En inventario visible</th>
                                    <th>Estado Excel</th>
                                    <th>Estado conciliación</th>
                                    <th>Hallazgo</th>
                                    <th>Acción sugerida</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!rows.length ? (
                                    <tr>
                                        <td colSpan={17} className="text-center py-4">
                                            {loading ? 'Cargando...' : 'No hay datos del consolidado.'}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.id}</td>
                                            <td>{row.sheet_name || '—'}</td>
                                            <td>{row.excel_nombre || '—'}</td>
                                            <td>{row.excel_folio || '—'}</td>
                                            <td>{getFolioTypeLabel(row.excel_folio_type) || '—'}</td>
                                            <td>{row.matchedFolio?.name || '—'}</td>
                                            <td>{boolBadge(row.folio_exists)}</td>
                                            <td>{boolBadge(row.folio_is_active, 'Activo', 'Inactivo')}</td>
                                            <td>{row.assigned_item_id || '—'}</td>
                                            <td>{row.matchedItem?.name || '—'}</td>
                                            <td>{boolBadge(row.is_sold)}</td>
                                            <td>{boolBadge(row.is_courtesy)}</td>
                                            <td>{boolBadge(row.is_visible_in_inventory)}</td>
                                            <td>{row.excel_estado || '—'}</td>
                                            <td>
                                                <Badge bg={statusVariant(row.reconciliation_status)}>
                                                    {getStatusLabel(row.reconciliation_status)}
                                                </Badge>
                                            </td>
                                            <td>{row.finding_label || '—'}</td>
                                            <td>{row.suggested_action_label || '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <div className="text-muted small">
                        Página {page} de {totalPages} ({totalRows} registros)
                    </div>

                    <div className="d-flex gap-2 align-items-center flex-wrap">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage(1)}
                        >
                            «
                        </Button>

                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        >
                            Anterior
                        </Button>

                        {visiblePages[0] > 1 && (
                            <>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setPage(1)}
                                >
                                    1
                                </Button>
                                {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
                            </>
                        )}

                        {visiblePages.map((pageNumber) => (
                            <Button
                                key={pageNumber}
                                size="sm"
                                variant={pageNumber === page ? 'secondary' : 'outline-secondary'}
                                onClick={() => setPage(pageNumber)}
                            >
                                {pageNumber}
                            </Button>
                        ))}

                        {visiblePages[visiblePages.length - 1] < totalPages && (
                            <>
                                {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                                    <span className="px-1 text-muted">…</span>
                                )}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setPage(totalPages)}
                                >
                                    {totalPages}
                                </Button>
                            </>
                        )}

                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                        >
                            Siguiente
                        </Button>

                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage(totalPages)}
                        >
                            »
                        </Button>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default InventoryReconciliation;